// 라바 라군 배치: 라군 모양(타원 웅덩이 8개를 겹침), 섬, 데크, 두 바·간헐천 자리, 물 판정, 수심. 프로토타입 2141–2184, 2332–2346.
// three.js를 쓰지 않는 순수 TS: 자리·정원 판정을 나중에 방 서버로 옮길 수 있게 둔다.
import { rand } from '../../engine/math';
import type { NavGrid } from '../../engine/nav';

/** 프로토타입의 라군 배율. 좌표는 모두 이 배율을 곱한 값. */
export const K = 1.45;
export const LCENTER = { x: 0, z: -8 * K };
/** 처음 들어오는 자리. */
export const SPAWN = { x: 2 * K, z: 10 * K };
/** 타원 웅덩이: [x, z, 반지름x, 반지름z, 굴곡 시드]. */
export const LBLOBS = ([[0, -4, 11, 8, 1], [-13, -10, 4.6, 4.6, 2], [-18.5, -16.5, 4.2, 4.2, 3], [12, -12, 6.2, 5.2, 4], [3, -15.5, 5.4, 4.6, 5], [-6, 3.5, 5.2, 4.4, 6], [-8, -5, 4, 4, 7], [17, -6, 3.2, 3.2, 8]] as const)
  .map(([x, z, rx, rz, sd]) => [x * K, z * K, rx * K, rz * K, sd] as const);
/** 섬: [x, z, 반지름]. */
export const LISLANDS = ([[-2.5, -6, 2.4], [5.5, -3.8, 1.6], [8.5, -13.5, 2.1], [-12, -11.5, 1.5], [-1, -16.5, 1.4], [-17, -19, 1.2], [14, -17, 1.3]] as const)
  .map(([x, z, r]) => [x * K, z * K, r * K] as const);
/** 판자길 두 토막과 둥근 플랫폼 두 개 (물 위에 있어도 걷는 곳). */
export const DECK = {
  segs: ([[16, 4.6, 12.5, 3.2], [12.5, 3.2, 8.2, 2.0]] as const).map(([a, b, c, d]) => [a * K, b * K, c * K, d * K] as const),
  plat: [6 * K, 1.5 * K, 2.3 * K] as const,
  plat2: [-6 * K, -1.2 * K, 1.9 * K] as const,
};
/** 슬러시 바: 오두막, 서서 주문하는 자리, 카운터, 바라보는 방향. */
export const SLUSH = { hut: { x: 6 * K, z: 1.9 * K }, spot: { x: 6 * K, z: -1.4 * K }, counter: { x: 6 * K, y: 1.05, z: 1.9 * K - 1.35 }, face: 0 };
export const MASKBAR = { hut: { x: -6 * K, z: -1.6 * K }, spot: { x: -6 * K, z: 1.2 * K }, face: Math.PI };
/** 큰 간헐천 자리. */
export const GEY = { x: 16, z: -42 };
/** 하루 길이 (초). */
export const DAYLEN = 600;

/** 원 장애물 또는 사각형 장애물. */
export type Obstacle = { rect?: false; x: number; z: number; r: number } | { rect: true; x: number; z: number; hw: number; hd: number };

export function segDist(px: number, pz: number, x1: number, z1: number, x2: number, z2: number): number {
  const dx = x2 - x1, dz = z2 - z1, l2 = dx * dx + dz * dz;
  let u = ((px - x1) * dx + (pz - z1) * dz) / l2;
  u = Math.max(0, Math.min(1, u));
  return Math.hypot(px - x1 - u * dx, pz - z1 - u * dz);
}

export function onDeck(x: number, z: number): boolean {
  if (DECK.segs.some((s) => segDist(x, z, s[0], s[1], s[2], s[3]) < 0.9)) return true;
  return Math.hypot(x - DECK.plat[0], z - DECK.plat[1]) < DECK.plat[2] || Math.hypot(x - DECK.plat2[0], z - DECK.plat2[1]) < DECK.plat2[2];
}

/** 섬·데크를 빼지 않은 라군 안쪽. */
export function inLagoonRaw(x: number, z: number): boolean {
  for (const [cx, cz, rx, rz, sd] of LBLOBS) {
    const a = Math.atan2(z - cz, x - cx), k = 1 + 0.1 * Math.sin(3 * a + sd) + 0.06 * Math.sin(5 * a + sd * 2);
    const u = (x - cx) / (rx * k), v = (z - cz) / (rz * k);
    if (u * u + v * v < 1) return true;
  }
  return false;
}

export const inIsland = (x: number, z: number): boolean =>
  LISLANDS.some(([cx, cz, r]) => Math.hypot(x - cx, z - cz) < r * (1 + 0.15 * Math.sin(3 * Math.atan2(z - cz, x - cx) + cx)));

export const lagoonWaterAt = (x: number, z: number): boolean => inLagoonRaw(x, z) && !inIsland(x, z) && !onDeck(x, z);

/** 물가에서 칸 거리로 잰 수심 지도 (물 밖은 0, 물가에서 멀수록 깊다). 보행 격자와 같은 칸. */
export function buildDepth(nav: NavGrid): Float32Array {
  const { x0, z0, cs, nx, nz } = nav.cfg, depth = new Float32Array(nx * nz), q: number[] = [];
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const k = j * nx + i, cx = x0 + (i + 0.5) * cs, cz = z0 + (j + 0.5) * cs;
    if (lagoonWaterAt(cx, cz)) depth[k] = 1e9; else { depth[k] = 0; q.push(k); }
  }
  for (let h = 0; h < q.length; h++) {
    const k = q[h]!, i = k % nx, j = (k / nx) | 0;
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const ni = i + di, nj = j + dj;
      if (ni < 0 || nj < 0 || ni >= nx || nj >= nz) continue;
      const nk = nj * nx + ni;
      if (depth[nk]! > depth[k]! + cs) { depth[nk] = depth[k]! + cs; q.push(nk); }
    }
  }
  return depth;
}

export interface Water { nav: NavGrid; depth: Float32Array }

export function depthAt(W: Water, x: number, z: number): number {
  const { nx, nz } = W.nav.cfg, i = W.nav.cellI(x), j = W.nav.cellJ(z);
  if (i < 0 || j < 0 || i >= nx || j >= nz) return 0;
  return W.depth[j * nx + i]!;
}

/** 0 = 땅, 1 = 헤엄칠 만큼 깊은 물. */
export const lagoonWet = (W: Water, x: number, z: number): number => Math.max(0, Math.min(1, (depthAt(W, x, z) - 0.45) / 0.9));

/** 무작위 자리. wantWater: true면 깊은 물(1.1 이상), false면 물 밖, 없으면 아무 데나. near가 있으면 그 근처부터. */
export function lagoonRandom(W: Water, near?: { x: number; z: number } | null, wantWater?: boolean | null): { x: number; z: number } {
  for (let i = 0; i < 90; i++) {
    let x: number, z: number;
    if (near && i < 55) { x = near.x + rand(-10, 10); z = near.z + rand(-10, 10); } else { x = rand(-32, 32); z = rand(-36, 13); }
    if (W.nav.blockedAt(x, z)) continue;
    const w = lagoonWaterAt(x, z);
    if (wantWater === true && (!w || depthAt(W, x, z) < 1.1)) continue;
    if (wantWater === false && w) continue;
    return { x, z };
  }
  return { ...SPAWN };
}
